import { useState } from 'react';
import { APP_NAME } from '@/branding';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useFirewallRules, type FirewallRule, type FirewallRuleSet } from '@/hooks/useFirewallRules';

/**
 * Firewall tab — the unified rule-list lens: rules parsed from the device's
 * latest captured config. DeviceDetails shows the tab only where enforcement
 * is modeled; a deep link elsewhere lands on the reason below. The implicit
 * default renders as a real row, per the design's "mark, never hide".
 */
export default function DeviceFirewallTab({ deviceId }: { deviceId: string | undefined }) {
  const { data, isLoading, isError } = useFirewallRules(deviceId);
  // Sets whose interface lists are open, by id.
  const [openScopes, setOpenScopes] = useState<ReadonlySet<string>>(new Set());
  const toggleScope = (id: string) => {
    setOpenScopes((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load firewall rules.</AlertDescription>
      </Alert>
    );
  }

  if (data.enforcement !== 'modeled') {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        {data.enforcement === 'none'
          ? 'This platform cannot filter traffic — no enforcement here.'
          : (data.reason ??
            `Enforcement on this device is not visible to ${APP_NAME} yet — unknown, not absence. If the platform carries ACLs, they still apply; ${APP_NAME} just cannot model them until a parser exists for this vendor.`)}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {data.mock && <Badge variant="secondary">mock rules</Badge>}
        <span>
          {/* The denominator is filter lines, not the whole config: the claim is
              that we understood this device's filters, not its entire config. */}
          coverage: {data.coverage.inScope - data.coverage.unaccountedLines}/{data.coverage.inScope} filter lines
          {data.coverage.unaccountedLines > 0 && ` — ${String(data.coverage.unaccountedLines)} not understood, so rules may be missing below`}
          <span className="text-fg-subtle"> of {data.coverage.totalLines} captured</span>
        </span>
        {data.source && (
          <span className="ml-auto text-xs text-fg-subtle">
            snapshot {data.source.snapshotId.slice(0, 8)} · captured {new Date(data.source.capturedAt).toLocaleString()}
          </span>
        )}
      </div>

      {data.ruleSets.map((rs) => (
        <div key={rs.id} className="mt-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold">{rs.name}</span>
            <Badge variant="outline">{rs.family}</Badge>
            <span className="text-fg-subtle">
              {rs.hook}
              {rs.interfaces ? (
                <>
                  {' · '}
                  <BoundInterfaces ends={rs.interfaces} open={openScopes.has(rs.id)} onToggle={() => { toggleScope(rs.id); }} />
                </>
              ) : rs.scope ? ` · ${rs.scope}` : ''}
            </span>
            {rs.stateful && <Badge variant="outline">stateful</Badge>}
            {/* Set-level lints. `unbound` is the one that matters: a set nothing
                references never runs, however correct its rules look. */}
            {rs.lints.map((l) => (
              <Badge key={l.kind} variant={l.kind === 'unbound' ? 'destructive' : 'secondary'} title={l.message}>
                {l.kind === 'unbound' ? 'never runs — bound to nothing' : l.message}
              </Badge>
            ))}
            <span className="ml-auto text-xs text-muted-foreground">
              default: <span className="font-semibold text-sev-1">{rs.defaultAction}</span> (last match)
              {rs.defaultAssumed && <span title="No default-action in the config; assumed. Verdicts resting on it are conditional."> · assumed</span>}
            </span>
          </div>
          {rs.interfaces && openScopes.has(rs.id) && <InterfaceLists ends={rs.interfaces} />}
          <div className="mt-2 rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {['#', 'Action', 'Proto', 'Source', 'Destination', 'Service', 'Hits', ''].map((h) => (
                    <th key={h} className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rs.rules.map((rule) => (
                  <RuleRow key={`${String(rule.seq)}-${rule.implicit ? 'implicit' : 'explicit'}`} rule={rule} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/** `→ 192.168.42.1`: what a NAT rule rewrites this column to. */
type InterfaceEnds = NonNullable<FirewallRuleSet['interfaces']>;

/** More interfaces than this at one end show as a count you can open. */
const INLINE_INTERFACES = 3;

function sameEnds({ from, to }: InterfaceEnds): boolean {
  return from !== null && to !== null && from.length === to.length && from.every((n, i) => n === to[i]);
}

function endLabel(names: string[] | null): string {
  if (names === null) return 'any interface';
  return names.length <= INLINE_INTERFACES ? names.join(', ') : `${String(names.length)} interfaces`;
}

/**
 * Where a zone pair applies. A zone binds each of its interfaces to each of
 * the other zone's, so the ends are named when short and counted when long —
 * the count opens to the names below the header.
 */
function BoundInterfaces({ ends, open, onToggle }: { ends: InterfaceEnds; open: boolean; onToggle: () => void }) {
  const long = [ends.from, ends.to].some((names) => names !== null && names.length > INLINE_INTERFACES);
  const to = !sameEnds(ends)
    ? endLabel(ends.to)
    : ends.to !== null && ends.to.length > INLINE_INTERFACES
      ? `same ${String(ends.to.length)}`
      : 'same';
  const text = `${endLabel(ends.from)} → ${to}`;
  if (!long) return <>{text}</>;
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="underline decoration-dotted underline-offset-2 hover:text-foreground">
      {text} {open ? '▾' : '▸'}
    </button>
  );
}

function InterfaceLists({ ends }: { ends: InterfaceEnds }) {
  const list = (names: string[] | null) => (names === null ? 'any interface' : names.join(', '));
  return (
    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 text-xs text-fg-subtle">
      <dt>from</dt>
      <dd>{list(ends.from)}</dd>
      <dt>to</dt>
      <dd>{sameEnds(ends) ? 'the same interfaces' : list(ends.to)}</dd>
    </dl>
  );
}

function RewrittenTo({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  return <span className="ml-1 font-medium text-foreground">→ {value}</span>;
}

function RuleRow({ rule }: { rule: FirewallRule }) {
  const cell = cn('py-2 px-3 border-t border-border-subtle', rule.implicit && 'border-t-2 border-dashed border-border italic text-fg-subtle');
  return (
    // A disabled rule is dimmed and struck, never hidden: it occupies its
    // sequence number on the device, and dropping it would renumber what the
    // operator sees there.
    <tr className={cn(rule.implicit && 'bg-bg-subtle', !rule.enabled && 'opacity-50 line-through')}>
      <td className={cn(cell, 'font-mono')}>{rule.implicit ? '—' : rule.seq}</td>
      <td className={cell}>
        <span
          className={cn(
            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold',
            // Only deny/reject block. jump, return, goto, nat and log-only
            // decide nothing on their own, so they read neutral, not red.
            rule.action === 'permit'
              ? 'bg-sev-4-soft text-sev-4'
              : rule.action === 'deny' || rule.action === 'reject'
                ? 'bg-sev-1-soft text-sev-1'
                : 'bg-muted text-muted-foreground'
          )}
        >
          {rule.action}
        </span>
      </td>
      <td className={cn(cell, 'font-mono')}>{rule.protocol}</td>
      <td className={cn(cell, 'font-mono')}>
        {rule.source.objectName && <span className="font-sans font-medium">{rule.source.objectName} </span>}
        <span className={cn(rule.source.objectName && 'text-fg-subtle')}>{rule.source.text}</span>
        <RewrittenTo value={rule.translation?.source} />
      </td>
      <td className={cn(cell, 'font-mono')}>
        {rule.destination.objectName && <span className="font-sans font-medium">{rule.destination.objectName} </span>}
        <span className={cn(rule.destination.objectName && 'text-fg-subtle')}>{rule.destination.text}</span>
        <RewrittenTo value={rule.translation?.destination} />
      </td>
      <td className={cn(cell, 'font-mono')}>
        {rule.service ?? '—'}
        <RewrittenTo value={rule.translation?.service} />
      </td>
      <td className={cn(cell, 'font-mono')}>{rule.hits?.toLocaleString() ?? '—'}</td>
      <td className={cell}>
        <div className="flex flex-wrap gap-1">
          {rule.implicit && <span className="text-xs">implicit — device default, not in config</span>}
          {!rule.enabled && <span className="text-xs">disabled on the device — never matches</span>}
          {rule.provenance.derivedFrom && (
            <span
              className="inline-flex rounded-full bg-sev-3-soft px-2 py-0.5 text-[11px] font-medium text-sev-3"
              title={`Reconstructed from ${rule.provenance.derivedFrom}. The device compiles this rule; ${APP_NAME} modelled that compiler rather than reading its output, so verdicts resting on it are conditional.`}
            >
              derived from {rule.provenance.derivedFrom}
            </span>
          )}
          {rule.lints.map((l) => (
            <span
              key={l.kind}
              /* `redundant` is deliberately not styled as a fault: the rule is
                 dead, but every rule covering it agrees, so deleting it changes
                 no verdict. Colouring it like `shadowed` would send someone
                 hunting for a policy conflict that does not exist. */
              className={
                l.kind === "shadowed"
                  ? "inline-flex rounded-full bg-sev-3-soft px-2 py-0.5 text-[11px] font-medium text-sev-3"
                  : "inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
              }
              title={l.message}
            >
              {l.kind}
            </span>
          ))}
          {rule.extensions.map((e) => (
            <span key={`${e.vendor}:${e.key}`} className="inline-flex rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
              {e.vendor}:{e.key}={e.value}
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}
